import React, { useState, useEffect } from "react";

const MultipleChoiceQuestion = ({
  question,
  answers,
  onAnswerChange,
  onOtherTextChange,
  questionIndex,
}) => {
  const handleOptionChange = (optionId, isChecked) => {
    const currentAnswers = answers[question.id] || [];
    let newAnswers;

    if (isChecked) {
      // Add option if not already selected
      if (!currentAnswers.includes(optionId)) {
        newAnswers = [...currentAnswers, optionId];
      } else {
        newAnswers = currentAnswers;
      }
    } else {
      // Remove option if selected
      newAnswers = currentAnswers.filter((id) => id !== optionId);
    }

    onAnswerChange(question.id, newAnswers);
  };

  const isOptionSelected = (optionId) => {
    const currentAnswers = answers[question.id] || [];
    return currentAnswers.includes(optionId);
  };

  // State for "Other" text input values
  const [otherTextValues, setOtherTextValues] = useState({});

  // Initialize other text values from existing answers
  useEffect(() => {
    if (answers[question.id] && Array.isArray(answers[question.id])) {
      const otherOption = question.options.find(
        (option) => option.text && option.text.toLowerCase().includes("other")
      );

      if (otherOption && isOptionSelected(otherOption.id)) {
        // Check if we have stored text for this option
        const storedText = answers[`${question.id}_other_text`] || "";
        setOtherTextValues((prev) => ({
          ...prev,
          [otherOption.id]: storedText,
        }));
      }
    }
  }, [answers, question.id, question.options]);

  const handleOtherTextChange = (optionId, text) => {
    setOtherTextValues((prev) => ({
      ...prev,
      [optionId]: text,
    }));

    // Store the text value in answers
    if (onOtherTextChange) {
      onOtherTextChange(question.id, text);
    } else {
      onAnswerChange(`${question.id}_other_text`, text);
    }
  };

  return (
    <div className="bg-gray-100 p-4 sm:p-6 lg:p-8 mb-6 no-copy w-full overflow-hidden">
      {/* Contextual Information */}
      {question.context && (
        <div className="mb-4 sm:mb-6">
          <p className="text-gray-700 text-base leading-relaxed question-content">
            {question.context}
          </p>
        </div>
      )}

      {/* Question Text */}
      <div className="mb-4 sm:mb-6">
        <p className="text-gray-900 text-lg font-bold leading-relaxed question-content">
          {question.text || question.question}
        </p>
      </div>

      {/* Options */}
      <div className="space-y-0 question-options">
        {question.options &&
          question.options.map((option, optionIndex) => {
            const isSelected = isOptionSelected(option.id || optionIndex);
            const isOtherOption =
              option.text && option.text.toLowerCase().includes("other");

            return (
              <div key={option.id || optionIndex}>
                <label className="flex items-start gap-3 p-3 cursor-pointer hover:bg-gray-50 transition-colors duration-150 no-copy w-full min-w-0">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={(e) =>
                      handleOptionChange(
                        option.id || optionIndex,
                        e.target.checked
                      )
                    }
                    className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2 flex-shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <span
                      className="text-gray-800 text-base break-words whitespace-normal"
                      style={{
                        overflowWrap: "anywhere",
                        wordBreak: "break-word",
                      }}
                    >
                      {String.fromCharCode(65 + optionIndex)}.{" "}
                      {option.text || option.option}
                    </span>
                  </div>
                </label>

                {/* Text input for "Other" option when selected */}
                {isOtherOption && isSelected && (
                  <div className="ml-8 mr-3 mb-3">
                    <input
                      type="text"
                      value={otherTextValues[option.id || optionIndex] || ""}
                      onChange={(e) =>
                        handleOtherTextChange(
                          option.id || optionIndex,
                          e.target.value
                        )
                      }
                      placeholder="Please specify..."
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base"
                    />
                  </div>
                )}

                {/* Dotted separator line between options */}
                {optionIndex < question.options.length - 1 && (
                  <div className="border-t border-dotted border-gray-300 mx-3"></div>
                )}
              </div>
            );
          })}
      </div>

      {/* Selection Status removed as requested */}
    </div>
  );
};

export default MultipleChoiceQuestion;
